import { useAtomValue } from 'jotai'
import useApprovalStatus from 'src/common/contracts/hooks/token/useApprovalStatus'
import useGasEstimate from 'src/common/contracts/hooks/useGasEstimate'

import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'
import useTakeMultipleOffers from 'src/common/contracts/hooks/dotc/useTakeOffer/useTakeMultipleOffers'
import { offersToOrders } from 'src/common/utils/offers/offersToOrders'
import { PreparedTransaction } from 'thirdweb'

import { useCallback, useMemo } from 'react'
import useTakeSingleOffer from 'src/common/contracts/hooks/dotc/useTakeOffer/useTakeSingleOffer'
import useConfig from 'src/config/hooks/useConfig'
import {
  buyTokenAmountAtom,
  buyTokenAtom,
  offersToTakeAtom,
  sellTokenAmountAtom,
  sellTokenAtom,
  skipOfferToTakeIdsAtom,
} from '../../../stores/trade-store'
import { applyPercentage } from 'src/common/utils/big-utils'

const useSwapTrade = () => {
  const { smartAccountInfo, isLoading: smartAccountLoading } = useSmartAccount()
  const config = useConfig()

  const sellToken = useAtomValue(sellTokenAtom)
  const buyToken = useAtomValue(buyTokenAtom)
  const sellTokenAmount = useAtomValue(sellTokenAmountAtom)
  const buyTokenAmount = useAtomValue(buyTokenAmountAtom)
  const skipOfferToTakeIds = useAtomValue(skipOfferToTakeIdsAtom)
  const offersToTake = useAtomValue(offersToTakeAtom)

  const spender = useMemo(() => {
    return config.contractsAddresses.dotcContractAddress
  }, [config.contractsAddresses.dotcContractAddress])

  const {
    approveTokenTx,
    needsApproval,
    refetchAllowance,
    loading: allowanceLoading,
  } = useApprovalStatus({
    token: sellToken,
    amountToSpend: sellTokenAmount,
    owner: smartAccountInfo?.address,
    spender,
  })

  const takeMultipleOffers = useTakeMultipleOffers()
  const takeSingleOffer = useTakeSingleOffer()

  const takeOffersTx = useCallback(() => {
    const filteredOffersToTake = offersToTake.filter(
      offer => !skipOfferToTakeIds?.includes(offer.id)
    )
    if (filteredOffersToTake.length <= 1) {
      return takeSingleOffer({
        offer: filteredOffersToTake[0],
      }) as PreparedTransaction[]
    }
    if (filteredOffersToTake.length > 1) {
      const minAmountToWithdraw =
        buyToken?.type === 'ERC20'
          ? applyPercentage(buyTokenAmount, -0.05)
          : buyTokenAmount

      return takeMultipleOffers({
        assetToSell: sellToken,
        assetToBuy: buyToken,
        amountToPay: sellTokenAmount,
        minAmountToWithdraw: minAmountToWithdraw,
        orders: offersToOrders(filteredOffersToTake),
      }) as PreparedTransaction[]
    }
  }, [
    buyToken,
    buyTokenAmount,
    offersToTake,
    skipOfferToTakeIds,
    sellToken,
    sellTokenAmount,
    takeMultipleOffers,
    takeSingleOffer,
  ])

  const transaction = approveTokenTx || takeOffersTx()

  const { gasEstimateInEther, estimateGas } = useGasEstimate({
    transaction,
  })

  return {
    takeOffersTx,
    approveTokenTx,
    needsApproval,
    gasEstimateInEther,
    refetchGasEstimate: estimateGas,
    refetchAllowance,
    loading: allowanceLoading || smartAccountLoading || !sellToken || !buyToken,
  }
}

export default useSwapTrade
